// What the picked file is, read from its first bytes in the browser (DESIGN_SPEC §11.2 picked line, §12.15).
//   parsePe(bytes) → PeInfo | null     DOS header → e_lfanew (0x3C) → "PE\0\0" → COFF header → optional header magic
//                                      → 40-byte section headers (Name, PointerToRawData, SizeOfRawData); bounds-checked
//   describeType(bytes, name, pe?)     "PE32+ x86-64" · "PE32 i386 .NET" · "ELF 64-bit x86-64" · "PDF 1.7" · "PowerShell script" · "data"
//   cleanSectionName(raw, index)       printable ASCII only (section names are attacker-controlled)

type PeSection = { name: string; offset: number; size: number };

export type PeInfo = {
  format: "PE32" | "PE32+";
  machine: string;
  dll: boolean;
  driver: boolean;
  dotnet: boolean;
  subsystem: "GUI" | "console" | "native" | "EFI" | null;
  /** End of the headers: SizeOfHeaders, capped at the first section's raw offset. */
  headerEnd: number;
  /** Sections with raw data, in file order. */
  sections: PeSection[];
  /** End of the last section's raw data: anything after it is overlay. */
  end: number;
};

const MACHINES: Record<number, string> = {
  0x14c: "i386",
  0x8664: "x86-64",
  0xaa64: "ARM64",
  0xa641: "ARM64EC",
  0x1c0: "ARM",
  0x1c4: "ARM",
  0x200: "IA-64",
  0x5064: "RISC-V64",
};

const u16 = (b: Uint8Array, o: number) => b[o] | (b[o + 1] << 8);
const u32 = (b: Uint8Array, o: number) => (b[o] | (b[o + 1] << 8) | (b[o + 2] << 16) | (b[o + 3] << 24)) >>> 0;

export function cleanSectionName(raw: Uint8Array, index: number): string {
  let s = "";
  for (let i = 0; i < raw.length && raw[i] !== 0; i++) {
    const c = raw[i];
    s += c >= 0x20 && c < 0x7f ? String.fromCharCode(c) : "?";
  }
  s = s.trim();
  return s || `#${index + 1}`;
}

export function parsePe(b: Uint8Array): PeInfo | null {
  if (b.length < 0x40 || b[0] !== 0x4d || b[1] !== 0x5a) return null; // "MZ"
  const pe = u32(b, 0x3c);
  if (pe < 0x40 || pe + 24 > b.length) return null;
  if (b[pe] !== 0x50 || b[pe + 1] !== 0x45 || b[pe + 2] !== 0 || b[pe + 3] !== 0) return null; // "PE\0\0"
  const coff = pe + 4;
  const machineId = u16(b, coff);
  const count = u16(b, coff + 2);
  const optSize = u16(b, coff + 16);
  const characteristics = u16(b, coff + 18);
  const opt = coff + 20;
  if (count === 0 || count > 96 || opt + 2 > b.length) return null;
  const magic = u16(b, opt);
  if (magic !== 0x10b && magic !== 0x20b) return null;
  const plus = magic === 0x20b;

  let headerEnd = opt + optSize + count * 40;
  let subsystem: PeInfo["subsystem"] = null;
  let dotnet = false;
  if (opt + 70 <= b.length && optSize >= 70) {
    headerEnd = Math.max(headerEnd, u32(b, opt + 60)); // SizeOfHeaders
    const ss = u16(b, opt + 68);
    subsystem = ss === 2 || ss === 9 ? "GUI" : ss === 3 ? "console" : ss === 1 ? "native" : ss >= 10 && ss <= 13 ? "EFI" : null;
    const nDirs = plus ? opt + 108 : opt + 92;
    const dirs = plus ? opt + 112 : opt + 96;
    if (nDirs + 4 <= b.length && u32(b, nDirs) > 14 && dirs + 15 * 8 <= opt + optSize && dirs + 15 * 8 <= b.length) {
      dotnet = u32(b, dirs + 14 * 8) !== 0 && u32(b, dirs + 14 * 8 + 4) !== 0; // CLR runtime header
    }
  }

  const table = opt + optSize;
  if (table + count * 40 > b.length) return null;
  const sections: PeSection[] = [];
  for (let i = 0; i < count; i++) {
    const s = table + i * 40;
    const size = u32(b, s + 16);
    const offset = u32(b, s + 20);
    if (size === 0 || offset === 0) continue; // .bss-like: no bytes in the file
    sections.push({ name: cleanSectionName(b.subarray(s, s + 8), i), offset, size });
  }
  sections.sort((x, y) => x.offset - y.offset);
  if (sections.length) headerEnd = Math.min(headerEnd, sections[0].offset);
  const end = sections.reduce((m, s) => Math.max(m, s.offset + s.size), headerEnd);

  return {
    format: plus ? "PE32+" : "PE32",
    machine: MACHINES[machineId] ?? `machine 0x${machineId.toString(16)}`,
    dll: (characteristics & 0x2000) !== 0,
    driver: subsystem === "native",
    dotnet,
    subsystem,
    headerEnd,
    sections,
    end,
  };
}

const ascii = (b: Uint8Array, o: number, n: number) => String.fromCharCode(...b.subarray(o, o + n));
const startsWith = (b: Uint8Array, sig: readonly number[], at = 0) => b.length >= at + sig.length && sig.every((v, i) => b[at + i] === v);

const ELF_MACHINES: Record<number, string> = { 0x03: "i386", 0x3e: "x86-64", 0x28: "ARM", 0xb7: "AArch64", 0xf3: "RISC-V", 0x08: "MIPS" };

const SCRIPT_TYPES: Record<string, string> = {
  ps1: "PowerShell script",
  psm1: "PowerShell module",
  psd1: "PowerShell data",
  bat: "batch script",
  cmd: "batch script",
  vbs: "VBScript",
  vbe: "encoded VBScript",
  js: "JScript",
  jse: "encoded JScript",
  wsf: "Windows Script File",
  hta: "HTML application",
  sh: "shell script",
  bash: "shell script",
  py: "Python script",
  pl: "Perl script",
  html: "HTML",
  htm: "HTML",
  xml: "XML",
  json: "JSON",
  txt: "text",
};

const ZIP_TYPES: Record<string, string> = {
  docx: "Word document (zip)",
  docm: "Word document, macros (zip)",
  xlsx: "Excel workbook (zip)",
  xlsm: "Excel workbook, macros (zip)",
  pptx: "PowerPoint (zip)",
  pptm: "PowerPoint, macros (zip)",
  jar: "Java archive",
  apk: "Android package",
  msix: "app package (zip)",
  appx: "app package (zip)",
};

const OLE_TYPES: Record<string, string> = {
  doc: "Word 97–2003 document",
  xls: "Excel 97–2003 workbook",
  ppt: "PowerPoint 97–2003",
  msi: "Windows Installer package",
  msg: "Outlook message",
};

function extOf(name: string): string {
  const dot = name.lastIndexOf(".");
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : "";
}

function isText(b: Uint8Array): boolean {
  const n = Math.min(b.length, 8192);
  if (n === 0) return false;
  let control = 0;
  for (let i = 0; i < n; i++) {
    const c = b[i];
    if (c === 0) return false;
    if (c < 0x09 || (c > 0x0d && c < 0x20 && c !== 0x1b)) control++;
  }
  return control / n < 0.02;
}

export function describeType(b: Uint8Array, name: string, pe: PeInfo | null = parsePe(b)): string {
  const ext = extOf(name);
  if (pe) {
    const parts = [pe.format, pe.machine];
    if (pe.dll) parts.push("DLL");
    if (pe.driver) parts.push("driver");
    if (pe.dotnet) parts.push(".NET");
    return parts.join(" ");
  }
  if (startsWith(b, [0x4d, 0x5a])) return "MS-DOS executable";
  if (startsWith(b, [0x7f, 0x45, 0x4c, 0x46]) && b.length >= 20) {
    const bits = b[4] === 2 ? "64-bit" : "32-bit";
    const machine = b[5] === 2 ? (b[18] << 8) | b[19] : b[18] | (b[19] << 8);
    return `ELF ${bits} ${ELF_MACHINES[machine] ?? `machine 0x${machine.toString(16)}`}`;
  }
  if (startsWith(b, [0x25, 0x50, 0x44, 0x46, 0x2d])) {
    const v = ascii(b, 5, 3);
    return /^\d\.\d$/.test(v) ? `PDF ${v}` : "PDF";
  }
  if (startsWith(b, [0x50, 0x4b, 0x03, 0x04]) || startsWith(b, [0x50, 0x4b, 0x05, 0x06])) return ZIP_TYPES[ext] ?? "ZIP archive";
  if (startsWith(b, [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1])) return OLE_TYPES[ext] ?? "OLE2 compound file";
  if (startsWith(b, [0x4c, 0x00, 0x00, 0x00, 0x01, 0x14, 0x02, 0x00])) return "Windows shortcut";
  if (startsWith(b, [0x52, 0x61, 0x72, 0x21, 0x1a, 0x07])) return "RAR archive";
  if (startsWith(b, [0x37, 0x7a, 0xbc, 0xaf, 0x27, 0x1c])) return "7-Zip archive";
  if (startsWith(b, [0x1f, 0x8b])) return "gzip";
  if (startsWith(b, [0x4d, 0x53, 0x43, 0x46])) return "Cabinet archive";
  if (startsWith(b, [0x7b, 0x5c, 0x72, 0x74, 0x66])) return "RTF document";
  if (startsWith(b, [0x43, 0x44, 0x30, 0x30, 0x31], 0x8001)) return "ISO 9660 image";
  if (startsWith(b, [0x76, 0x68, 0x64, 0x78, 0x66, 0x69, 0x6c, 0x65])) return "VHDX image";
  if (startsWith(b, [0xff, 0xfe]) || startsWith(b, [0xfe, 0xff])) return `${SCRIPT_TYPES[ext] ?? "text"}, UTF-16`;
  if (isText(b.subarray(startsWith(b, [0xef, 0xbb, 0xbf]) ? 3 : 0))) {
    if (b[0] === 0x23 && b[1] === 0x21) {
      const line = ascii(b, 2, Math.min(b.length - 2, 48)).split(/[\r\n]/)[0].trim();
      const interp = line.split(/\s+/).find((w) => !w.endsWith("/env")) ?? line;
      return `script (${interp.split("/").pop() || "#!"})`;
    }
    return SCRIPT_TYPES[ext] ?? "text";
  }
  return "data";
}
